import {
  Field,
  Form,
  FormCheckbox,
  FormCurrencyField,
  FormRow,
  FormText,
} from '@chargeafter/forms';
import { Button } from '@chargeafter/ui';
import { Grid, Typography, Box, styled } from '@mui/material';

import { FC, useEffect, useState } from 'react';
import { CartTotalIcon, DeleteIcon, SaveIcon, TurnArrowIcon } from '../icons';
import { formatCurrency } from '../formattingUtils';
import {
  CartItem,
  useGetApprovedAmount,
  useGetFinancialProductType,
} from '../services/flowManager';
import { SubmissionErrors } from 'final-form';
import {
  calcCart,
  calcItem,
  useAdvancedCartDetails,
  useEditedItem,
} from '../services/cartManager';
import { validateCartItem } from '../validations';
import { saveToUserSession } from '../services/userSessionStorageManager';
import logger from '../services/loggerManager';
import { mixed, number, object, string } from 'yup';
import { SELF_FINANCING_PRODUCT, ButtonName } from '../constants';
import { isNumberAllowed } from '../formsUtils';
import { sendButtonClickEvent } from '../services/trackerManager';
import { TrackerButton } from '../components/TrackerButtonWrapper';
import { useResetCurrentModal } from '../services/modalManager';
import { useGetCheckoutConfiguration } from '../services/lendersConfigurationManager';

const StyledBox = styled(Box)({
  width: '100%',
});

const StyledHeader = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  backgroundColor: theme.palette.secondary.light,
  padding: theme.spacing(2),
  textTransform: 'uppercase',
}));

const AddItemButton = styled(Button)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  alignItems: 'center',
  justifyContent: 'center',
}));

const FormContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4, 8, 0, 8),
  minHeight: theme.spacing(10),
}));

const GridForm = styled(Grid)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: `8fr 8fr 4fr 3fr`,
  gap: theme.spacing(3),
  paddingTop: theme.spacing(3),
  height: theme.spacing(9),
}));

const LeasableWarrantyContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  padding: theme.spacing(4, 0),
}));

const ShowWarrantyWrapper = styled(Box)({
  display: 'flex',
  gap: 2,
  alignItems: 'center',
});

const DeleteItemWrapper = styled(Box)({
  display: 'flex',
});

const DeleteItemButton = styled(TrackerButton)(({ theme }) => ({
  backgroundColor: theme.palette.error.main,
}));

const RemoveButton = styled(TrackerButton)(({ theme }) => ({
  height: theme.spacing(5),
}));

const AddWarrantyButton = styled(TrackerButton)(({ theme }) => ({
  width: 'fit-content',
  padding: theme.spacing(1, 2),
}));

interface CartItemModalProps {
  updateClose: (func: () => void) => void;
  close: () => void;
}

export const CartItemModal: FC<CartItemModalProps> = ({
  close,
  updateClose,
}) => {
  const [editedItem, setEditedItem] = useEditedItem();
  const editedItemPrice = editedItem ? calcItem(editedItem) : 0;
  const resetCurrentModal = useResetCurrentModal();
  const financialProductType = useGetFinancialProductType();
  const [advancedCartDetails, setAdvancedCartDetails] =
    useAdvancedCartDetails();
  const [showWarranty, setShowWarranty] = useState(
    editedItem && editedItem?.warranty ? true : false,
  );
  const approvedAmount = useGetApprovedAmount();

  const selfFinancing =
    financialProductType && financialProductType === SELF_FINANCING_PRODUCT;

  const hasApprovedAmount = approvedAmount > 0;
  const maxAmount =
    hasApprovedAmount && !selfFinancing ? approvedAmount : 999999.99;

  let priceWithoutWarranty = 0;
  const currentTotalAmount = calcCart(advancedCartDetails);

  const itemNameValidator = string()
    .max(100, 'More than 100 characters is not allowed')
    .min(2, 'You must enter more than one character.')
    .matches(
      /^[a-zA-Z0-9 ]*$/,
      'You may have entered symbols, slashes, or asterisks which are not allowed.',
    );

  const skuValidator = string()
    .max(20, 'More than 20 characters are not allowed')
    .min(2, 'You must enter more than one character.')
    .matches(
      /^[a-zA-Z0-9 ]*$/,
      'You may have entered symbols, slashes, or asterisks which are not allowed.',
    );

  const cartItemValidationSchema = object().shape({
    name: itemNameValidator,
    sku: skuValidator,
    price: mixed().test(
      'min-amount',
      `Exceeded amount`,
      function (value, context) {
        priceWithoutWarranty =
          maxAmount +
          editedItemPrice -
          currentTotalAmount -
          (value || 0) * context.parent.quantity -
          (+context.parent?.warranty?.price || 0);
        return 0 <= priceWithoutWarranty;
      },
    ),
    quantity: number(),
    warranty: object({
      name: itemNameValidator,
      sku: skuValidator,
    }).when(['price', 'quantity'], {
      is: () => 0 <= priceWithoutWarranty,
      otherwise: () =>
        object().shape({
          price: mixed().test(
            'min-amount',
            `Exceeded amount`,
            function (value) {
              return 0 <= priceWithoutWarranty - (value || 0);
            },
          ),
        }),
    }),
  });

  const handleSubmitItem = (values: Record<string, any>) => {
    sendButtonClickEvent({ buttonName: ButtonName.SaveItem });

    const errors = validateCartItem(values, showWarranty);
    if (Object.keys(errors).length > 0) {
      return errors;
    }
    const item = values as CartItem;
    const cartWithoutEditedItem = [
      ...advancedCartDetails.items.filter((x) => x !== editedItem),
    ];

    const itemExistInItemsList = cartWithoutEditedItem.find(
      (item) =>
        item.sku.toLowerCase() === values.sku.toLowerCase() &&
        +item.price === +values.price,
    );

    let updatedCart = { ...advancedCartDetails };
    if (itemExistInItemsList) {
      updatedCart.items = cartWithoutEditedItem.map((item) =>
        item.sku.toLowerCase() === values.sku.toLowerCase() &&
        +item.price === +values.price
          ? { ...item, quantity: item.quantity + values.quantity }
          : item,
      );
    } else {
      updatedCart.items = [...cartWithoutEditedItem, item];
    }

    setAdvancedCartDetails(updatedCart);
    saveToUserSession('advancedCart', updatedCart);
    close();
    return;
  };

  const handleDeleteItem = () => {
    const deletedItem = {
      ...advancedCartDetails,
      items: advancedCartDetails.items.filter(
        (x) => x.sku !== editedItem?.sku && +x.price !== editedItem?.price,
      ),
    };
    const cartEmpty = deletedItem.items.length === 0;
    const updatedCart = {
      ...deletedItem,
      ...(cartEmpty && {
        taxAmount: 0,
        shippingAmount: 0,
        merchantOrderId: '',
        discounts: undefined,
      }),
    };

    setAdvancedCartDetails(updatedCart);
    saveToUserSession('advancedCart', updatedCart);
    close();
  };

  const handleRemoveWarranty = (
    submitErrors: SubmissionErrors,
    values: Record<string, any>,
  ) => {
    if (submitErrors) {
      submitErrors.warranty = {};
    }
    delete values.warranty;
    setShowWarranty(false);
  };

  const handleShowWarranty = () => {
    setShowWarranty(true);
  };

  const ltoSupported =
    useGetCheckoutConfiguration()?.lendersConfigurations?.find((c) =>
      c?.lenderProductTypes?.includes('LeaseToOwnProgram'),
    );

  useEffect(() => {
    updateClose(() => () => {
      editedItem && setEditedItem(undefined);
      resetCurrentModal();
      logger.log(`Closed Cart Item modal`, {
        filename: 'CartItemModal.tsx',
        function: 'closeModal',
      });
    });
  }, []);

  const saveItemFormatting = (values: Record<string, any>) => {
    const itemPrice =
      (values.price || 0) * values.quantity +
      ((showWarranty && +values?.warranty?.price) || 0);
    const preEditedItemPrice = editedItem ? calcItem(editedItem) : 0;

    const formattedApprovedAmount = formatCurrency(maxAmount);
    const totalAmount = itemPrice + currentTotalAmount - preEditedItemPrice;
    const formattedCartAmount = formatCurrency(totalAmount);

    return maxAmount < totalAmount
      ? `Cart limit is ${formattedApprovedAmount} (current cart ${formattedCartAmount})`
      : `${editedItem ? 'Save' : 'Add'} Item (${formatCurrency(
          (values?.price || 0) * values.quantity +
            (+values?.warranty?.price || 0),
        )})`;
  };

  return (
    <>
      <StyledBox>
        <StyledHeader variant="h6" data-testid="cart-title">
          {editedItem ? `edit item` : `add item`}
        </StyledHeader>
      </StyledBox>
      <Form
        validationSchema={cartItemValidationSchema}
        initialValues={
          (editedItem
            ? {
                ...editedItem,
              }
            : {
                quantity: 1,
                leasable: true,
              }) as Record<string, any>
        }
        onSubmit={handleSubmitItem}
        render={({ values, submitErrors, isSubmitDisabled, pristine }) => (
          <>
            <FormContainer>
              <GridForm>
                <FormText
                  name="name"
                  inputProps={{
                    maxLength: 101,
                  }}
                  data-testid="item-name"
                  label="Item name"
                />
                <FormText
                  inputProps={{
                    maxLength: 21,
                  }}
                  name="sku"
                  label="SKU"
                  data-testid="item-sku"
                />
                <FormCurrencyField
                  name="price"
                  label="Unit Price"
                  data-testid="item-price"
                  decimalScale={2}
                  isAllowed={isNumberAllowed}
                />
                <Field
                  name="quantity"
                  render={({ input }) => (
                    <ItemsCounter
                      onDecrement={() =>
                        input.onChange(values.quantity - 1 || 1)
                      }
                      onIncrement={() => input.onChange(values.quantity + 1)}
                      amount={values.quantity}
                    />
                  )}
                />
              </GridForm>
              {showWarranty && (
                <GridForm alignItems="flex-start">
                  <ShowWarrantyWrapper>
                    <TurnArrowIcon />
                    <FormText
                      name="warranty.name"
                      data-testid="warranty-name"
                      label="Warranty name"
                      inputProps={{
                        maxLength: 101,
                      }}
                    />
                  </ShowWarrantyWrapper>
                  <FormText
                    name="warranty.sku"
                    data-testid="warranty-sku"
                    label="SKU"
                    inputProps={{
                      maxLength: 21,
                    }}
                  />
                  <FormCurrencyField
                    name="warranty.price"
                    data-testid="warranty-price"
                    label="Unit Price"
                    isAllowed={isNumberAllowed}
                    decimalScale={2}
                  />
                  <RemoveButton
                    buttonName={ButtonName.RemoveItem}
                    variant="text"
                    data-testid="remove-button"
                    onClick={() => {
                      handleRemoveWarranty(submitErrors, values);
                    }}
                  >
                    Remove
                  </RemoveButton>
                </GridForm>
              )}
              <LeasableWarrantyContainer>
                {ltoSupported && (
                  <Box display="flex" flexDirection="column">
                    <FormCheckbox
                      data-testid="leasable-checkbox"
                      name="leasable"
                      label="Leasable"
                    />
                    <Typography variant="caption">
                      * Please make sure to check this box for leasable items.
                    </Typography>
                  </Box>
                )}
                {!showWarranty && (
                  <Box>
                    <AddWarrantyButton
                      icon="add"
                      buttonName={ButtonName.AddItemWarranty}
                      variant="textWithIcon"
                      onClick={handleShowWarranty}
                      data-testid="add-warranty-button"
                    >
                      <Typography variant="h6">Add Warranty</Typography>
                    </AddWarrantyButton>
                  </Box>
                )}
              </LeasableWarrantyContainer>
            </FormContainer>
            <FormRow>
              {editedItem ? (
                <DeleteItemWrapper>
                  <DeleteItemButton
                    buttonName={ButtonName.DeleteItem}
                    data-testid="delete-item-button"
                    onClick={handleDeleteItem}
                  >
                    <DeleteIcon />
                    Delete Item
                  </DeleteItemButton>
                  <Button
                    data-testid="save-item-button"
                    disabled={isSubmitDisabled}
                    type="submit"
                  >
                    <SaveIcon />
                    {saveItemFormatting(values)}
                  </Button>
                </DeleteItemWrapper>
              ) : (
                <AddItemButton
                  disabled={isSubmitDisabled}
                  data-testid="add-item-cart"
                  type="submit"
                >
                  <>
                    <CartTotalIcon fontSize="small" />
                    {saveItemFormatting(values)}
                  </>
                </AddItemButton>
              )}
            </FormRow>
          </>
        )}
      />
    </>
  );
};

const ButtonsSetContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  gap: theme.spacing(1),
  paddingTop: theme.spacing(1),
}));

const RoundedButton = styled(TrackerButton)(({ theme }) => ({
  borderRadius: '100%',
  width: theme.spacing(3),
  backgroundColor: 'white',
  height: theme.spacing(3),
  padding: 0,
  minWidth: 0,
}));

interface ItemsCounterProps {
  amount: number;
  onIncrement: () => void;
  onDecrement: () => void;
}

const ItemsCounter = ({
  amount,
  onIncrement,
  onDecrement,
}: ItemsCounterProps) => {
  return (
    <ButtonsSetContainer>
      <RoundedButton
        buttonName={ButtonName.DecreaseQuantity}
        variant="secondary"
        data-testid="decrement-button"
        onClick={onDecrement}
      >
        -
      </RoundedButton>
      <Typography data-testid="quantity" variant="body2">
        {amount}
      </Typography>
      <RoundedButton
        buttonName={ButtonName.IncreaseQuantity}
        data-testid="increment-button"
        variant="secondary"
        onClick={onIncrement}
      >
        +
      </RoundedButton>
    </ButtonsSetContainer>
  );
};
